import React, { useState, useEffect } from "react";
import { useSkillsQuery } from "../../graphql/generated/graphql";

interface SkillOption {
  value: string;
  label: string;
}

interface SkillsAutoCompleteProps {
  onSkillsChange: (skills: SkillOption[]) => void;
}

export function SkillsAutoComplete({
  onSkillsChange,
}: SkillsAutoCompleteProps) {
  const [options, setOptions] = useState<SkillOption[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<SkillOption[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<SkillOption[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { data, loading, error } = useSkillsQuery({
    variables: {
      first: 10,
    },
  });

  useEffect(() => {
    if (data?.skills?.nodes) {
      const newOptions = data.skills.nodes.map((skill) => ({
        value: skill.title,
        label: skill.title,
      }));
      setOptions(newOptions);
      setFilteredOptions(newOptions);
    }
  }, [data]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !selectedSkills.some((skill) => skill.value === option.value)
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(
        options.filter(
          (option) =>
            !selectedSkills.some((skill) => skill.value === option.value)
        )
      );
    }
  }, [searchTerm, options, selectedSkills]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
  };

  const handleSelect = (skill: SkillOption) => {
    setSelectedSkills((prev) => [...prev, skill]);
    setSearchTerm("");
    setShowSuggestions(false);
    onSkillsChange([...selectedSkills, skill]);
  };

  const handleRemoveSkill = (skill: SkillOption) => {
    const updatedSkills = selectedSkills.filter((s) => s.value !== skill.value);
    setSelectedSkills(updatedSkills);
    onSkillsChange(updatedSkills);
  };

  const handleAddNewSkill = () => {
    if (
      searchTerm.trim() &&
      !selectedSkills.some((skill) => skill.value === searchTerm.trim())
    ) {
      const newSkillOption = {
        value: searchTerm.trim(),
        label: searchTerm.trim(),
      };
      setSelectedSkills((prev) => [...prev, newSkillOption]);
      setSearchTerm("");
      onSkillsChange([...selectedSkills, newSkillOption]);
    }
  };

  const filteredAndNewSkill =
    searchTerm && !options.some((option) => option.value === searchTerm.trim())
      ? [
          ...filteredOptions,
          { value: searchTerm.trim(), label: searchTerm.trim() },
        ]
      : filteredOptions;

  return (
    <div className="skills-autocomplete">
      <input
        type="text"
        id="skills-search"
        placeholder="جستجوی مهارت..."
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => {
          setFilteredOptions(
            options.filter(
              (option) =>
                !selectedSkills.some((skill) => skill.value === option.value)
            )
          );
          setShowSuggestions(true);
        }}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        className="input"
      />
      {showSuggestions && filteredAndNewSkill.length > 0 && (
        <ul className="suggestions-list">
          {filteredAndNewSkill.map((option) => (
            <li
              key={option.value}
              onClick={() => handleSelect(option)}
              className="suggestion-item"
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
      <div className="selected-skills">
        {selectedSkills.map((skill) => (
          <span key={skill.value} className="selected-skill">
            <button
              type="button"
              onClick={() => handleRemoveSkill(skill)}
              className="remove-skill-btn"
            >
              ×
            </button>
            {skill.label}
          </span>
        ))}
      </div>
      {loading && <p className="loading-text">در حال بارگذاری...</p>}
      {error && <p className="error-text">خطایی رخ داده است.</p>}
      {searchTerm.trim() &&
        !options.some((option) => option.value === searchTerm.trim()) && (
          <button
            type="button"
            onClick={handleAddNewSkill}
            className="add-new-skill-btn"
          ></button>
        )}
    </div>
  );
}
